import type { ColumnDef } from "@tanstack/react-table";
import { msg } from "@lingui/core/macro";
import type { I18n } from "@lingui/core";
import { Link } from "@tanstack/react-router";
import { DataTableColumnHeader } from "@/components/data-table";
import { CostValue } from "@/components/recipes/cost/CostValue";
import { formatNumber } from "@/lib/materials/conversion-calc";
import type { RecipeUsageLine } from "@/lib/api/requests/recipes";

const CELL = "h-auto px-3 py-3";
const HEAD = "text-xs font-medium text-(--color-text-secondary)";
const TEXT = "text-sm text-(--color-text-primary)";

/** Columns for the recipe lines that use an ingredient or a prep recipe. */
export function getRecipeUsageColumns(i18n: I18n): ColumnDef<RecipeUsageLine, unknown>[] {
  return [
    {
      id: "recipe",
      accessorFn: (row) => row.recipeName,
      header: ({ column }) => <DataTableColumnHeader column={column} title={i18n._(msg`Recipe`)} className={HEAD} />,
      // A real link, so the recipe opens from the keyboard too; the row's own
      // click is a mouse shortcut to the same place.
      cell: ({ row }) => (
        <Link
          to="/recipes/$recipeId"
          params={{ recipeId: String(row.original.recipeId) }}
          onClick={(event) => event.stopPropagation()}
          className="text-sm font-semibold text-(--color-text-primary) underline-offset-2 hover:underline focus-visible:underline"
        >
          {row.original.recipeName}
        </Link>
      ),
      meta: { label: i18n._(msg`Recipe`), className: CELL },
    },
    {
      id: "type",
      accessorFn: (row) => row.recipeType,
      header: ({ column }) => <DataTableColumnHeader column={column} title={i18n._(msg`Type`)} className={HEAD} />,
      cell: ({ row }) => (
        <span className={TEXT}>{row.original.recipeType === "PLATED" ? i18n._(msg`Plated`) : i18n._(msg`Prep`)}</span>
      ),
      meta: { label: i18n._(msg`Type`), className: CELL },
    },
    {
      id: "quantity",
      accessorFn: (row) => row.quantity,
      header: ({ column }) => <DataTableColumnHeader column={column} title={i18n._(msg`Quantity`)} className={HEAD} />,
      cell: ({ row }) => (
        <span className={TEXT}>
          {formatNumber(row.original.quantity, 3)} {row.original.unit}
        </span>
      ),
      meta: { label: i18n._(msg`Quantity`), className: CELL },
    },
    {
      id: "cost",
      accessorFn: (row) => row.extendedCost ?? -1,
      header: ({ column }) => <DataTableColumnHeader column={column} title={i18n._(msg`Line cost`)} className={HEAD} />,
      cell: ({ row }) => <CostValue value={row.original.extendedCost} reason={row.original.unavailableReason} />,
      meta: { label: i18n._(msg`Line cost`), className: `${CELL} text-right` },
    },
  ];
}
